import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Heart, MessageCircle } from 'lucide-react';

import { ShareMenu } from '@/components/ShareMenu';
import { ZapButton } from '@/components/ZapButton';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import type { EngagementStats } from '@/hooks/useEngagement';
import { HIGHLIGHT_KIND, highlightPath, type Highlight } from '@/lib/highlights';
import { cn } from '@/lib/utils';

const buttonClass =
  'flex items-center gap-1.5 h-7 px-2 text-xs text-muted-foreground hover:text-foreground hover:bg-transparent';

interface EngagementRowProps {
  highlight: Highlight;
  stats?: EngagementStats;
  className?: string;
}

/** Reaction / comment / zap / share actions for a highlight card. */
export function EngagementRow({ highlight, stats, className }: EngagementRowProps) {
  const { user } = useCurrentUser();
  const { mutateAsync: createEvent } = useNostrPublish();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Optimistic local override, reset whenever fresh stats arrive.
  const [override, setOverride] = useState<boolean | null>(null);
  useEffect(() => setOverride(null), [stats?.reactions, stats?.myReactionId]);

  const reacted = override ?? !!stats?.myReactionId;
  const baseCount = stats?.reactions ?? 0;
  const reactionCount = override === null
    ? baseCount
    : override
      ? stats?.myReactionId ? baseCount : baseCount + 1
      : stats?.myReactionId ? baseCount - 1 : baseCount;

  const toggleReaction = async () => {
    if (!user) {
      toast({ title: 'Log in to react', description: 'Connect your Nostr account to react to highlights.' });
      return;
    }

    const wasReacted = reacted;
    const previousReactionId = stats?.myReactionId;
    setOverride(!wasReacted);

    try {
      if (wasReacted && previousReactionId) {
        await createEvent({
          kind: 5,
          content: '',
          tags: [
            ['e', previousReactionId],
            ['k', '7'],
          ],
        });
      } else {
        await createEvent({
          kind: 7,
          content: '+',
          tags: [
            ['e', highlight.event.id, '', highlight.event.pubkey],
            ['p', highlight.event.pubkey],
            ['k', String(HIGHLIGHT_KIND)],
          ],
        });
      }
    } catch {
      setOverride(null);
      toast({
        title: 'Reaction failed',
        description: 'Your relays may be unreachable. Try again in a moment.',
        variant: 'destructive',
      });
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['nostr', 'engagement'] });
  };

  return (
    <div className={cn('flex items-center gap-1 -ml-2', className)}>
      <Button
        variant="ghost"
        size="sm"
        onClick={toggleReaction}
        aria-label={reacted ? 'Remove reaction' : 'React'}
        aria-pressed={reacted}
        className={cn(buttonClass, reacted && 'text-red-500 hover:text-red-500')}
      >
        <Heart className={cn('size-4', reacted && 'fill-current')} />
        {reactionCount > 0 && <span>{reactionCount}</span>}
      </Button>

      <Button variant="ghost" size="sm" asChild className={buttonClass}>
        <Link to={highlightPath(highlight)} aria-label="View comments">
          <MessageCircle className="size-4" />
          {stats && stats.comments > 0 && <span>{stats.comments}</span>}
        </Link>
      </Button>

      <ZapButton
        target={highlight.event}
        zapData={stats ? { count: stats.zapCount, totalSats: stats.zapSats, isLoading: false } : undefined}
        className="text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-md hover:bg-transparent [&]:cursor-pointer"
      />

      <span className="ml-auto">
        <ShareMenu highlight={highlight} />
      </span>
    </div>
  );
}
